import React, { useCallback } from 'react'

import { GuestContact } from 'src/apollo/onlineOrdering'
import Button from 'src/shared/components/common/button'
import UserAvatar from 'src/public/components/default_template/nav/UserAvatar'
import VerifyEmailModal from 'src/public/components/default_template/online_ordering/account/verifyEmailModal/VerifyEmailModal'
import { ScreenWidth, useIsMobile } from 'src/shared/js/utils/WindowContext'
import { t } from 'src/translations'

import { EditIcon, WarningOutlineIcon } from '@toasttab/buffet-pui-icons'
import { useModal } from 'shared/components/common/modal'
import { EditAccountModal } from '../EditAccountModal'
import { useEmailVerification } from '../hooks/useEmailVerification'
import { showEmailUpdateSuccess } from '../utils/accountNotifications'

export const AccountInformation = ({
  contact,
  setIsLoadingEmailVerification,
  refetchGuestData
}: {
  contact?: GuestContact
  setIsLoadingEmailVerification: (isLoading: boolean) => void
  refetchGuestData: () => Promise<any>
}) => {
  const isMobile = useIsMobile(ScreenWidth.EXTRA_SMALL)
  const {
    isOpen: isEditAccountModalOpen,
    onOpen: onOpenEditAccountModal,
    onClose: onCloseEditAccountModal
  } = useModal()
  const {
    isOpen: isVerifyEmailModalOpen,
    onOpen: onOpenVerifyEmailModal,
    onClose: onCloseVerifyEmailModal
  } = useModal()
  const { sendVerificationCode } = useEmailVerification()

  const onSendEmailVerificationCode = useCallback(() => {
    sendVerificationCode(contact?.email ?? '')
    onOpenVerifyEmailModal()
  }, [contact?.email, onOpenVerifyEmailModal, sendVerificationCode])

  const handleEmailVerificationComplete = useCallback(
    async (didUpdateEmail: boolean) => {
      if (didUpdateEmail) {
        showEmailUpdateSuccess()
      }
      onCloseVerifyEmailModal()
    },
    [onCloseVerifyEmailModal]
  )

  if (!contact) return null

  //We only look at the first email because we only support one email per guest.
  //If we ever support multiple emails the first email will be the primary email and we will likely want to update this logic.
  const isEmailVerified = contact?.contactEmails?.[0]?.isVerified

  return (
    <div className='accountInfo'>
      <div className='accountDetails'>
        <div className='avatar'>
          <UserAvatar
            firstName={contact.firstName}
            lastName={contact.lastName}
            useThemeColors={false}
          />
          {isMobile ? (
            <button
              className='editButtonMobile'
              onClick={onOpenEditAccountModal}
            >
              <EditIcon aria-label={t('profile-information.edit-profile')} />
            </button>
          ) : (
            <button
              className='editButtonDesktop'
              onClick={onOpenEditAccountModal}
            >
              {t('profile-information.edit-profile')}
            </button>
          )}
        </div>
        <div>
          <div className='header'>
            <div
              className='accountName'
              data-testid='account-name'
            >{`${contact.firstName} ${contact.lastName}`}</div>
            {isEmailVerified && <VerifiedIcon size={isMobile ? 18 : 22} />}
          </div>
          {isEmailVerified ? (
            <div className='accountEmail'>{contact.email}</div>
          ) : (
            <div className='emailVerificationPending'>
              {t('profile-information.email-verification-pending')}
            </div>
          )}
          <div className='accountPhone' data-testid='account-phone'>
            {contact.phone}
          </div>
        </div>
      </div>
      {!isEmailVerified && (
        <div className='verifyEmail'>
          <div className='leftContent'>
            <WarningOutlineIcon accessibility='decorative' />
            <div className='verifyEmailContent'>
              <div className='verifyEmailHeading'>
                {t('profile-information.please-verify-email')}
              </div>
              <div className='verifyEmailDescription'>
                {t('profile-information.email-verification-explanation')}
              </div>
            </div>
          </div>
          <Button
            className='sendCodeButton'
            onClick={onSendEmailVerificationCode}
          >
            {t('profile-information.send-code')}
          </Button>
        </div>
      )}
      <EditAccountModal
        isOpen={isEditAccountModalOpen}
        onClose={onCloseEditAccountModal}
        setIsLoadingEmailVerification={setIsLoadingEmailVerification}
        contact={contact}
        refetchGuestData={refetchGuestData}
      />
      <VerifyEmailModal
        email={contact?.email || ''}
        isOpen={isVerifyEmailModalOpen}
        onCloseModal={handleEmailVerificationComplete}
        resendCode={sendVerificationCode}
      />
    </div>
  )
}

const VerifiedIcon = ({ size }: { size: number }) => (
  <svg
    xmlns='http://www.w3.org/2000/svg'
    width={size}
    height={size}
    viewBox='0 0 17 17'
    fill='none'
    aria-label={t('profile-information.email-verified')}
  >
    <path
      d='M6.85922 0.608957C7.50144 -0.202979 8.73362 -0.202992 9.37583 0.608957C9.79787 1.14269 10.5105 1.35211 11.1541 1.13142C12.1334 0.79541 13.1699 1.46152 13.2713 2.49177C13.3378 3.16891 13.8245 3.73078 14.4852 3.89314C15.4907 4.13991 16.003 5.26064 15.5311 6.1822C15.2209 6.78785 15.3266 7.52337 15.7948 8.01716C16.5073 8.76843 16.3317 9.98827 15.4364 10.5084C14.8481 10.8503 14.5398 11.5264 14.6668 12.1949C14.8599 13.212 14.0527 14.1428 13.0184 14.0963C12.3386 14.0658 11.7134 14.4681 11.4588 15.0992C11.0713 16.059 9.88966 16.4061 9.04477 15.8082C8.48936 15.4149 7.74569 15.4149 7.19028 15.8082C6.34539 16.4061 5.16372 16.059 4.77622 15.0992C4.52164 14.4682 3.89639 14.0658 3.21665 14.0963C2.18235 14.1428 1.37515 13.212 1.56821 12.1949C1.6952 11.5264 1.38691 10.8503 0.798677 10.5084C-0.0966491 9.98827 -0.272252 8.76843 0.440279 8.01716C0.90844 7.52337 1.01414 6.78784 0.703951 6.1822C0.232103 5.26076 0.743605 4.14013 1.74887 3.89314C2.40981 3.73093 2.89722 3.16906 2.96372 2.49177C3.06511 1.46152 4.10165 0.795411 5.0809 1.13142C5.72452 1.35209 6.43719 1.14268 6.85922 0.608957ZM11.7206 4.78278C11.4444 4.57873 11.0555 4.63753 10.8514 4.91364L7.06626 10.0338L5.1727 8.00837C4.93818 7.75769 4.54459 7.74475 4.29379 7.97907C4.04301 8.21368 4.0299 8.60719 4.2645 8.85798L6.66879 11.4283C6.79493 11.5631 6.97472 11.6352 7.15903 11.6246C7.34312 11.6138 7.51323 11.5219 7.6229 11.3736L11.8514 5.6529C12.0555 5.37673 11.9967 4.98691 11.7206 4.78278Z'
      fill='#227800'
    />
  </svg>
)

export default AccountInformation
